/**
 * Auto Upload Hook
 *
 * Automatically uploads images and PDFs to Firebase Storage when added to content blocks.
 * Handles upload progress, conversion to storage blocks, and capture mode message creation.
 *
 * @module features/chat/hooks/chat-input/useAutoUpload
 */

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { uploadImageToStorage } from "../../services/image-upload-service";
import { uploadPdfToStorage } from "../../services/pdf-upload-service";
import { createCaptureContentMessage } from "../../services/capture-service";
import type {
  Base64ContentBlock,
  StorageImageBlock,
  StoragePdfBlock,
  ContentBlock,
} from "../../types/messages";

// =============================================================================
// TYPES
// =============================================================================

export interface UseAutoUploadOptions {
  contentBlocks: ContentBlock[];
  setContentBlocks: (
    blocks: ContentBlock[] | ((prev: ContentBlock[]) => ContentBlock[]),
  ) => void;
  userId: string | undefined;
  conversationId: string | undefined;
  captureMode: boolean;
}

export interface UseAutoUploadReturn {
  /** Reference to track processed blocks (for clearing on conversation change) */
  processedBlocksRef: React.MutableRefObject<Set<string>>;
  /** Reference to track saved block IDs in capture mode */
  savedBlockIdsRef: React.MutableRefObject<Set<string>>;
  /** True while an image or PDF upload is in flight (sending must wait) */
  hasPendingUploads: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for automatic file uploads to Firebase Storage.
 *
 * Features:
 * - Auto-detects new base64 images and PDFs in content blocks
 * - Uploads to Firebase Storage with progress tracking
 * - Replaces base64 blocks with storage blocks
 * - In capture mode: creates Firestore messages after upload
 * - Bulletproof deduplication via stable block ID tracking
 */
export function useAutoUpload({
  contentBlocks,
  setContentBlocks,
  userId,
  conversationId,
  captureMode,
}: UseAutoUploadOptions): UseAutoUploadReturn {
  // Track blocks that have started processing - NEVER removed during hook lifecycle
  // Using Set<string> where string is the block's unique id
  const processedBlocksRef = useRef<Set<string>>(new Set());
  const savedBlockIdsRef = useRef<Set<string>>(new Set());

  // Track blocks currently being uploaded (for concurrent upload prevention)
  const uploadingBlocksRef = useRef<Set<string>>(new Set());
  // Rendered count of in-flight uploads, so sending can wait for them: a
  // message sent mid-upload would carry the image inline as base64.
  const [pendingUploads, setPendingUploads] = useState(0);

  useEffect(() => {
    if (!userId || !conversationId) return;

    // Process each block synchronously to check if it needs upload
    // The actual upload is async but the decision is sync
    for (const block of contentBlocks) {
      // Type narrow to Base64ContentBlock
      if (!("source_type" in block) || block.source_type !== "base64") {
        continue;
      }

      // Only process images and PDFs
      if (
        !(
          block.type === "image" ||
          (block.type === "file" && block.mime_type === "application/pdf")
        )
      ) {
        continue;
      }

      // Skip blocks that are still being processed (instant preview feature)
      if ("processing" in block && block.processing === true) {
        continue;
      }

      const base64Block = block as Base64ContentBlock;

      // CRITICAL: Block must have an id for deduplication
      if (!base64Block.id) {
        console.warn(
          "[useAutoUpload] Block without id found, skipping:",
          block.metadata?.name,
        );
        continue;
      }

      const blockId = base64Block.id;

      // Skip if already processed OR currently being uploaded
      if (
        processedBlocksRef.current.has(blockId) ||
        uploadingBlocksRef.current.has(blockId)
      ) {
        continue;
      }

      // Mark as being processed SYNCHRONOUSLY before any async work
      processedBlocksRef.current.add(blockId);
      uploadingBlocksRef.current.add(blockId);
      setPendingUploads((n) => n + 1);

      // Start upload (fire and forget - errors handled inside)
      if (block.type === "image") {
        uploadImage(
          base64Block,
          userId,
          conversationId,
          setContentBlocks,
          captureMode,
          savedBlockIdsRef,
        ).finally(() => {
          uploadingBlocksRef.current.delete(blockId);
          setPendingUploads((n) => n - 1);
        });
      } else if (
        block.type === "file" &&
        block.mime_type === "application/pdf"
      ) {
        uploadPdf(
          base64Block,
          userId,
          conversationId,
          setContentBlocks,
          captureMode,
          savedBlockIdsRef,
        ).finally(() => {
          uploadingBlocksRef.current.delete(blockId);
          setPendingUploads((n) => n - 1);
        });
      }
    }
  }, [contentBlocks, userId, conversationId, setContentBlocks, captureMode]);

  return {
    processedBlocksRef,
    savedBlockIdsRef,
    hasPendingUploads: pendingUploads > 0,
  };
}

// =============================================================================
// HELPERS
// =============================================================================

async function uploadImage(
  block: Base64ContentBlock,
  userId: string,
  conversationId: string,
  setContentBlocks: (
    blocks: ContentBlock[] | ((prev: ContentBlock[]) => ContentBlock[]),
  ) => void,
  captureMode: boolean,
  savedBlockIdsRef: React.MutableRefObject<Set<string>>,
): Promise<void> {
  const blockId = block.id!; // Guaranteed to exist by caller

  try {
    const base64Data = block.data.split(",")[1] || block.data;
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let j = 0; j < byteCharacters.length; j++) {
      byteNumbers[j] = byteCharacters.charCodeAt(j);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: block.mime_type });

    const uploadResult = await uploadImageToStorage(
      blob,
      userId,
      conversationId,
      (progress) => {
        setContentBlocks((prev: ContentBlock[]) =>
          prev.map((b) => {
            if ("id" in b && b.id === blockId && b.source_type === "base64") {
              return { ...b, uploadProgress: progress };
            }
            return b;
          }),
        );
      },
      block.metadata?.name,
    );

    const thumbnailToPreserve = block.thumbnail;
    const uploadDate = new Date().toISOString();

    // Replace base64 block with storage block
    setContentBlocks((prev: ContentBlock[]) =>
      prev.map((b) => {
        if ("id" in b && b.id === blockId && b.source_type === "base64") {
          const currentMetadata = b.metadata || {};
          const newBlock: StorageImageBlock = {
            type: "image",
            source_type: "storage",
            fileName: uploadResult.fileName,
            relative_gcs_path: `images/${uploadResult.fileName}`,
            mime_type: block.mime_type,
            thumbnail: thumbnailToPreserve,
            metadata: {
              ...currentMetadata,
              size: currentMetadata.size,
              name: uploadResult.fileName,
              storagePath: uploadResult.storagePath,
              thumbnail: thumbnailToPreserve,
              uploadDate,
              ...(currentMetadata.photoDate && {
                photoDate: currentMetadata.photoDate,
              }),
            },
            creatingMessage: captureMode,
            id: blockId, // Preserve for Firestore message ID
          };
          return newBlock;
        }
        return b;
      }),
    );

    console.log("✅ Image auto-uploaded to storage:", uploadResult.fileName);

    // In capture mode: create message in Firestore
    if (captureMode) {
      try {
        console.log("[CaptureMode] Image metadata:", {
          photoDate: block.metadata?.photoDate,
          uploadDate,
          fileName: uploadResult.fileName,
          blockId,
        });

        const response = await createCaptureContentMessage(
          userId,
          conversationId,
          {
            type: "image",
            file_name: uploadResult.fileName,
            relative_gcs_path: uploadResult.storagePath,
            mime_type: block.mime_type,
            thumbnail: thumbnailToPreserve,
            metadata: {
              name: block.metadata?.name,
              size: block.metadata?.size,
              uploadDate,
              ...(block.metadata?.photoDate && {
                photoDate: block.metadata.photoDate,
              }),
            },
          },
          blockId, // Use pre-generated ID as Firestore document ID
        );

        setContentBlocks((prev: ContentBlock[]) =>
          prev.map((b) => {
            if ("id" in b && b.id === blockId && b.source_type === "storage") {
              return {
                ...b,
                messageId: response.message_id,
                creatingMessage: undefined,
              };
            }
            return b;
          }),
        );

        savedBlockIdsRef.current.add(blockId);
        console.log(
          `[CaptureMode] ✅ Image message created: ${response.message_id}`,
        );
      } catch (error) {
        console.error("[CaptureMode] Failed to create image message:", error);
        setContentBlocks((prev: ContentBlock[]) =>
          prev.map((b) => {
            if ("id" in b && b.id === blockId && b.source_type === "storage") {
              return { ...b, creatingMessage: undefined };
            }
            return b;
          }),
        );
        toast.error("Fout bij opslaan", {
          description:
            "De afbeelding kon niet worden opgeslagen. Probeer het opnieuw.",
        });
      }
    }
  } catch (error) {
    console.error("Failed to auto-upload image:", error);
    toast.error("Upload failed", {
      description: `Failed to upload ${block.metadata?.name || "image"}`,
    });
  }
}

async function uploadPdf(
  block: Base64ContentBlock,
  userId: string,
  conversationId: string,
  setContentBlocks: (
    blocks: ContentBlock[] | ((prev: ContentBlock[]) => ContentBlock[]),
  ) => void,
  captureMode: boolean,
  savedBlockIdsRef: React.MutableRefObject<Set<string>>,
): Promise<void> {
  const blockId = block.id!; // Guaranteed to exist by caller

  try {
    const base64Data = block.data.split(",")[1] || block.data;
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let j = 0; j < byteCharacters.length; j++) {
      byteNumbers[j] = byteCharacters.charCodeAt(j);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: block.mime_type });

    const uploadResult = await uploadPdfToStorage(
      blob,
      userId,
      conversationId,
      (progress) => {
        setContentBlocks((prev: ContentBlock[]) =>
          prev.map((b) => {
            if ("id" in b && b.id === blockId && b.source_type === "base64") {
              return { ...b, uploadProgress: progress };
            }
            return b;
          }),
        );
      },
      block.metadata?.name || block.metadata?.filename,
    );

    const pdfThumbnail = block.thumbnail;

    // Replace base64 block with storage block
    setContentBlocks((prev: ContentBlock[]) =>
      prev.map((b) => {
        if ("id" in b && b.id === blockId && b.source_type === "base64") {
          const currentMetadata = b.metadata || {};
          const newBlock: StoragePdfBlock = {
            type: "file",
            source_type: "storage",
            fileName: uploadResult.fileName,
            mime_type: "application/pdf",
            thumbnail: pdfThumbnail,
            metadata: {
              ...currentMetadata,
              filename: uploadResult.fileName,
              storagePath: uploadResult.storagePath,
              thumbnail: pdfThumbnail,
            },
            creatingMessage: captureMode,
            id: blockId, // Preserve for Firestore message ID
          };
          return newBlock;
        }
        return b;
      }),
    );

    console.log("✅ PDF auto-uploaded to storage:", uploadResult.fileName);

    // In capture mode: create message in Firestore
    if (captureMode) {
      try {
        const response = await createCaptureContentMessage(
          userId,
          conversationId,
          {
            type: "file",
            file_name: uploadResult.fileName,
            relative_gcs_path: uploadResult.storagePath,
            mime_type: "application/pdf",
            thumbnail: pdfThumbnail,
            metadata: {
              name: block.metadata?.name || block.metadata?.filename,
              size: block.metadata?.size,
            },
          },
          blockId, // Use pre-generated ID as Firestore document ID
        );

        setContentBlocks((prev: ContentBlock[]) =>
          prev.map((b) => {
            if ("id" in b && b.id === blockId && b.source_type === "storage") {
              return {
                ...b,
                messageId: response.message_id,
                creatingMessage: undefined,
              };
            }
            return b;
          }),
        );

        savedBlockIdsRef.current.add(blockId);
        console.log(
          `[CaptureMode] ✅ PDF message created: ${response.message_id}`,
        );
      } catch (error) {
        console.error("[CaptureMode] Failed to create PDF message:", error);
        setContentBlocks((prev: ContentBlock[]) =>
          prev.map((b) => {
            if ("id" in b && b.id === blockId && b.source_type === "storage") {
              return { ...b, creatingMessage: undefined };
            }
            return b;
          }),
        );
        toast.error("Fout bij opslaan", {
          description:
            "Het PDF bestand kon niet worden opgeslagen. Probeer het opnieuw.",
        });
      }
    }
  } catch (error) {
    console.error("Failed to auto-upload PDF:", error);
    toast.error("Upload failed", {
      description: `Failed to upload ${block.metadata?.name || block.metadata?.filename || "PDF"}`,
    });
  }
}
